import { ref, computed, unref } from 'vue';

/**
 * Generic collapsible open/close state. Tracks a manual expanded flag and OR-combines
 * it with an optional `forceExpanded` override (e.g. "contains the active route" or
 * "matched a search"), so a section can be kept open regardless of manual toggling.
 *
 * @param {Object} [options]
 * @param {boolean} [options.defaultExpanded=false] - Initial manual state.
 * @param {boolean|import('vue').Ref<boolean>} [options.forceExpanded=false] - Reactive override forcing the section open.
 * @returns {{
 *   isExpanded: import('vue').ComputedRef<boolean>,
 *   setExpanded: (value: boolean) => void,
 *   toggle: () => void,
 * }}
 */
export function useCollapsible(options = {}) {
    const { defaultExpanded = false, forceExpanded = false } = options;

    const isManuallyExpanded = ref(defaultExpanded);

    const isExpanded = computed(() => unref(forceExpanded) || isManuallyExpanded.value);

    function setExpanded(value) {
        isManuallyExpanded.value = value;
    }

    function toggle() {
        setExpanded(!isManuallyExpanded.value);
    }

    return { isExpanded, setExpanded, toggle };
}
